import { Button, Typography } from '@/components';
import { NotepadTextIcon } from '@/components/atoms/icons';
import { ProgressHeader } from '@/components/molecules/header/ProgressHeader';
import { useTranslation, useUIColors } from '@/core';
import { DT_SCREEN } from '@/core/constants/dynatraceScreens';
import { useCatalogDynatraceView } from '@/core/hooks/useCatalogDynatraceView';
import { trackCatalogEvent } from '@/core/services/trackDynatraceCatalog';
import { logger } from '@/core/logger';
import { asyncStorage } from '@/core/storage';
import { REGISTRATION_TYPE, useManualRegistrationStore } from '@/modules/onboarding/registration/store/registrationStore';
import { useRegistrationProgress } from '@/modules/onboarding/registration/utils/registrationProgress';
import * as FileSystemLegacy from 'expo-file-system/legacy';
import { router } from 'expo-router';
import React, { useCallback, useEffect, useState } from 'react';
import { KeyboardAvoidingView, Platform, ScrollView, StyleSheet, TouchableOpacity, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

interface DocumentImages {
    emiratesIdFront?: string;
    emiratesIdBack?: string;
    passport?: string;
}

async function keepVerifiedDocumentUri(uri: string): Promise<string | null> {
    try {
        const cacheDir = (FileSystemLegacy as { cacheDirectory?: string }).cacheDirectory;
        const isLocalFile =
            uri.startsWith('file://') || (Boolean(cacheDir) && uri.startsWith(cacheDir ?? ''));
        if (!isLocalFile) {
            return uri;
        }
        const fileInfo = await FileSystemLegacy.getInfoAsync(uri);
        if (fileInfo.exists) {
            return uri;
        }
        logger.warn('DocumentReviewScreen', `Stored file does not exist: ${uri}`);
        return null;
    } catch (error) {
        logger.error('DocumentReviewScreen', `Failed to verify file: ${uri}`, error);
        return uri;
    }
}

export function DocumentReviewScreen() {
    useCatalogDynatraceView(DT_SCREEN.DOCUMENT_REVIEW, 'document_review_viewed', true);
    const { t } = useTranslation();
    const colors = useUIColors();
    const insets = useSafeAreaInsets();
    const registrationStore = useManualRegistrationStore();
    const progress = useRegistrationProgress();

    const registrationType = registrationStore.registrationType;
    const formData = registrationStore.formData;
    const [documents, setDocuments] = useState<DocumentImages>({});

    // Load documents from local storage
    useEffect(() => {
        const loadDocuments = async () => {
            try {
                const stored = await asyncStorage.getItem('document.images');
                if (stored) {
                    const parsed = JSON.parse(stored) as DocumentImages;
                    const storedEntries = Object.entries(parsed).filter(
                        (entry): entry is [string, string] => Boolean(entry[1]),
                    );
                    const verifiedPairs = await Promise.all(
                        storedEntries.map(async ([key, uri]) => {
                            const verifiedUri = await keepVerifiedDocumentUri(uri);
                            if (!verifiedUri) {
                                return null;
                            }
                            return [key, verifiedUri] as const;
                        }),
                    );
                    const verifiedDocuments: DocumentImages = {};
                    for (const pair of verifiedPairs) {
                        if (!pair) {
                            continue;
                        }
                        verifiedDocuments[pair[0] as keyof DocumentImages] = pair[1];
                    }
                    setDocuments(verifiedDocuments);
                    logger.info('DocumentReviewScreen', 'Loaded documents from storage', {
                        hasEmiratesIdFront: !!verifiedDocuments.emiratesIdFront,
                        hasEmiratesIdBack: !!verifiedDocuments.emiratesIdBack,
                        hasPassport: !!verifiedDocuments.passport,
                    });
                }
            } catch (error) {
                logger.error('DocumentReviewScreen', 'Failed to load documents from storage', error);
            }
        };
        void loadDocuments();
    }, []);

    // Get user details from form data
    const userDetails = formData ? {
        firstName: formData.firstName,
        middleName: formData.middleName || '',
        lastName: formData.lastName,
        email: formData.email || '', // Email saved after verify-details API
        phoneNumber: formData.phone || '', // Phone saved after verify-details API
        emiratesIdNumber: formData.emiratesId || '',
        passportNumber: formData.passportNumber,
    } : {
        firstName: '',
        middleName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        emiratesIdNumber: '',
        passportNumber: '',
    };

    const handlePreview = useCallback(
        (documentType: 'emiratesIdFront' | 'emiratesIdBack' | 'passport') => {
            const uri = documents[documentType];
            if (!uri) {
                logger.warn('DocumentReviewScreen', `No document found for type: ${documentType}`);
                return;
            }

            // Use document upload screen in preview mode for captured documents (no auth required)
            trackCatalogEvent({
                screen: DT_SCREEN.DOCUMENT_REVIEW,
                actionName: 'document_review_edit',
                eventType: 'action',
            });
            router.push({
                pathname: '/document-upload',
                params: {
                    hideConfirm: 'true',
                    previewMode: 'true',
                    previewDocumentType: documentType,
                },
            });
        },
        [documents]
    );

    const handleSubmit = () => {
        trackCatalogEvent({
            screen: DT_SCREEN.DOCUMENT_REVIEW,
            actionName: 'document_review_confirm',
            eventType: 'action',
        });
        router.replace('/registration-success');
    };

    const DetailRow = ({ label, value }: { label: string; value: string }) => (
        <View style={styles.detailRow}>
            <Typography variant="subhead" color="fontTertiary" style={styles.detailLabel}>
                {label}
            </Typography>
            <Typography variant="body" color="fontPrimary" style={styles.detailValue}>
                {value}
            </Typography>
        </View>
    );

    const PreviewButton = ({ documentType }: { documentType: 'emiratesIdFront' | 'emiratesIdBack' | 'passport' }) => (
        <TouchableOpacity
            style={styles.previewButton}
            onPress={() => handlePreview(documentType)}
        >
            <View style={styles.previewButtonContent}>
                <View style={styles.previewIcon}>
                    <Typography variant="body" color="fontPrimary" style={styles.previewIconText}>
                        📷
                    </Typography>
                </View>
                <View style={styles.previewTextContainer}>
                    <Typography variant="body" color="fontPrimary" style={styles.previewText}>
                        {t('documentReview.preview')}
                    </Typography>
                    <View style={styles.previewUnderline} />
                </View>
            </View>
        </TouchableOpacity>
    );

    return (
        <View style={[styles.container, { backgroundColor: colors.backgroundLevel1 }]}>
            {/* Header */}
            <ProgressHeader
                progress={progress}
            />

            <KeyboardAvoidingView
                style={styles.keyboardAvoid}
                behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
            >
                <ScrollView
                    style={styles.scrollView}
                    contentContainerStyle={[
                        styles.scrollContent,
                        { paddingBottom: 100 } // Add padding to prevent content from being hidden behind the button
                    ]}
                    showsVerticalScrollIndicator={false}
                >
                    <View style={styles.content}>
                        {/* Page Header */}
                        <View style={styles.pageHeader}>
                            <View style={styles.iconContainer}>
                                <NotepadTextIcon size={40} color={colors.fontPrimary} />
                            </View>
                            <Typography variant="title1" color="fontPrimary" style={styles.pageTitle}>
                                {t('documentReview.title')}
                            </Typography>
                        </View>

                        {/* Details Card */}
                        <View style={styles.detailsCard}>
                            {/* Personal Information */}
                            <View style={styles.section}>
                                {Boolean(userDetails.firstName) && (
                                    <DetailRow label={t('documentReview.firstName')} value={userDetails.firstName} />
                                )}
                                {Boolean(userDetails.middleName) && (
                                    <DetailRow label={t('documentReview.middleName')} value={userDetails.middleName} />
                                )}
                                {Boolean(userDetails.lastName) && (
                                    <DetailRow label={t('documentReview.lastName')} value={userDetails.lastName} />
                                )}
                                {Boolean(userDetails.email) && (
                                    <DetailRow label={t('documentReview.email')} value={userDetails.email} />
                                )}
                                {Boolean(userDetails.phoneNumber) && (
                                    <DetailRow label={t('documentReview.phoneNumber')} value={userDetails.phoneNumber} />
                                )}
                            </View>

                            {/* Divider */}
                            <View style={styles.divider} />

                            {/* Emirates ID Section - Only show for UAE flow */}
                            {registrationType === REGISTRATION_TYPE.EMIRATES_ID && (
                                <>
                                    <View style={styles.section}>
                                        <DetailRow label={t('documentReview.emiratesIdNumber')} value={userDetails.emiratesIdNumber} />
                                        {documents.emiratesIdFront && (
                                            <View style={styles.documentRow}>
                                                <Typography variant="subhead" color="fontTertiary" style={styles.documentLabel}>
                                                    {t('documentViewer.frontSide')}
                                                </Typography>
                                                <PreviewButton documentType="emiratesIdFront" />
                                            </View>
                                        )}
                                        {documents.emiratesIdBack && (
                                            <View style={styles.documentRow}>
                                                <Typography variant="subhead" color="fontTertiary" style={styles.documentLabel}>
                                                    {t('documentViewer.backSide')}
                                                </Typography>
                                                <PreviewButton documentType="emiratesIdBack" />
                                            </View>
                                        )}
                                    </View>
                                    {documents.passport && (
                                        <>
                                            <View style={styles.divider} />
                                            <View style={styles.section}>
                                                <DetailRow label={t('documentReview.passportNumber')} value={userDetails.passportNumber} />
                                                <View style={styles.documentRow}>
                                                    <Typography variant="subhead" color="fontTertiary" style={styles.documentLabel}>
                                                        {t('documentReview.passportPicture')}
                                                    </Typography>
                                                    <PreviewButton documentType="passport" />
                                                </View>
                                            </View>
                                        </>
                                    )}
                                </>
                            )}

                            {/* Passport Section - Only show for passport flow */}
                            {registrationType === REGISTRATION_TYPE.PASSPORT && (
                                <View style={styles.section}>
                                    {Boolean(userDetails.passportNumber) && (
                                        <DetailRow label={t('documentReview.passportNumber')} value={userDetails.passportNumber} />
                                    )}
                                    {documents.passport && (
                                        <View style={styles.documentRow}>
                                            <Typography variant="subhead" color="fontTertiary" style={styles.documentLabel}>
                                                {t('documentReview.passportPicture')}
                                            </Typography>
                                            <PreviewButton documentType="passport" />
                                        </View>
                                    )}
                                </View>
                            )}
                        </View>
                    </View>
                </ScrollView>
            </KeyboardAvoidingView>

            {/* Submit Button - Bottom positioned */}
            <View style={[styles.bottomButtonContainer, { bottom: insets.bottom + 35 }]}>
                <Button
                    title={t('documentReview.submit')}
                    fullWidth={true}
                    onPress={handleSubmit}
                />
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
    },
    keyboardAvoid: {
        flex: 1,
    },
    scrollView: {
        flex: 1,
    },
    scrollContent: {
        flexGrow: 1,
    },
    content: {
        flex: 1,
        paddingHorizontal: 16,
        paddingTop: 32,
    },
    pageHeader: {
        flexDirection: 'column',
        gap: 16,
        marginBottom: 32,
    },
    iconContainer: {
        paddingHorizontal: 8,
    },
    pageTitle: {
        fontSize: 28,
        fontWeight: '500',
        lineHeight: 36,
    },
    detailsCard: {
        backgroundColor: '#ffffff',
        borderRadius: 16,
        borderWidth: 1,
        borderColor: '#b3b3b3',
        paddingHorizontal: 24,
        paddingVertical: 16,
    },
    section: {
        paddingVertical: 8,
    },
    detailRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 16,
    },
    detailLabel: {
        fontSize: 14,
        fontWeight: '500',
        color: '#666666',
    },
    detailValue: {
        fontSize: 16,
        fontWeight: '400',
        color: '#333333',
        textAlign: 'right',
    },
    divider: {
        height: 1,
        backgroundColor: '#ededed',
        marginVertical: 20,
    },
    documentRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 8,
    },
    documentLabel: {
        fontSize: 14,
        fontWeight: '500',
        color: '#666666',
        flex: 1,
    },
    previewButton: {
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderRadius: 4,
    },
    previewButtonContent: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
    },
    previewIcon: {
        width: 18,
        height: 18,
        alignItems: 'center',
        justifyContent: 'center',
    },
    previewIconText: {
        fontSize: 14,
    },
    previewTextContainer: {
        alignItems: 'center',
    },
    previewText: {
        fontSize: 16,
        fontWeight: '600',
        color: '#1a1a1a',
    },
    previewUnderline: {
        height: 1,
        backgroundColor: '#1a1a1a',
        width: '100%',
    },
    bottomButtonContainer: {
        position: 'absolute',
        left: 16,
        right: 16,
        zIndex: 10,
    },
});

export default DocumentReviewScreen;
